import { Injectable } from '@angular/core';
import { AnalyticPilotEventTypes } from '@core/enums/analytic/analytic-pilot-event-types.enum';
import { AnalyticBaseProperties } from '@core/models/analytic/analytic-base-properties.model';
import { User } from '@core/states/user/user-state.model';
import { DeviceDetectorService } from 'ngx-device-detector';

import { AppSettingsService } from '../app-settings/app-settings.service';
import { MonitorService } from '../monitor/monitor.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticService {
  private userInfo: User;

  private eventQueue: {
    event: AnalyticPilotEventTypes;
    properties: AnalyticBaseProperties;
  }[];

  constructor(
    private appSettingsService: AppSettingsService,
    private deviceService: DeviceDetectorService,
    private userService: UserService,
    public monitorService: MonitorService
  ) {
    this.eventQueue = [];
  }

  post<T extends AnalyticBaseProperties>(
    event: AnalyticPilotEventTypes,
    properties: T
  ): void {
    try {
      this.initBuildNumber(properties);
      this.initDeviceInformations(properties);
      this.initUserInformation(properties);

      if (!!properties.MasterContactId || this.userIdNotRequired(event)) {
        this.track(event, properties);
      } else {
        this.eventQueue.push({ event, properties });
      }
    } catch (e) {
      this.monitorService.logError({
        error: {
          ...e,
          name: 'AnalyticService Error catched'
        },
        properties: {
          message: 'post'
        }
      });
    }
  }
  private userIdNotRequired(event: AnalyticPilotEventTypes): boolean {
    return event === AnalyticPilotEventTypes.Unknown;
  }
  postError<T extends AnalyticBaseProperties>(
    error: Error,
    properties: T
  ): void {
    try {
      this.initBuildNumber(properties);
      this.initDeviceInformations(properties);
      this.initUserInformation(properties);
      this.trackError(error, properties);
    } catch (e) {
      this.monitorService.logError({
        error: {
          ...e,
          name: 'AnalyticService Error catched'
        },
        properties: {
          message: 'postError'
        }
      });
    }
  }

  private initBuildNumber(properties: AnalyticBaseProperties): void {
    if (
      !!this.appSettingsService.configuration &&
      !!this.appSettingsService.configuration.buildNumber
    ) {
      properties.AppVersion = this.appSettingsService.configuration.buildNumber;
    }
  }

  private initDeviceInformations(properties: AnalyticBaseProperties): void {
    if (!!this.deviceService && !!this.deviceService.device) {
      properties.DeviceType = this.deviceService.device;
    }
    if (!!this.deviceService && !!this.deviceService.os) {
      properties.ClientOS = this.deviceService.os;
    }
  }

  private initUserInformation(properties: AnalyticBaseProperties): void {
    if (!!this.userInfo && !!this.userInfo.salesForceMasterContactId) {
      properties.MasterContactId = this.userInfo.salesForceMasterContactId;
    }
  }

  public getUserAnalytic(): void {
    this.userService.getUser()?.subscribe((user: User) => {
      if (!!user && !!user.salesForceMasterContactId) {
        this.userInfo = user;
        this.trackEventQueue();
      }
    });
  }

  private trackEventQueue(): void {
    if (!!this.userInfo && !!this.userInfo.salesForceMasterContactId) {
      this.eventQueue.forEach((value) => {
        value.properties.MasterContactId =
          this.userInfo.salesForceMasterContactId;
        this.track(value.event, value.properties);
      });
      this.eventQueue = [];
    }
  }
  private track<T extends AnalyticBaseProperties>(
    event: AnalyticPilotEventTypes,
    properties: T
  ): void {
    this.monitorService.logEvent({
      name: event,
      properties
    });
  }

  private trackError<T extends AnalyticBaseProperties>(
    error: Error,
    properties: T
  ): void {
    this.monitorService.logError({
      error,
      properties
    });
  }
}
